<template>
  <div
    class="c-population-tab-populations-row"
    :class="{
      'c-population-tab-populations-row--modified': hasChanges,
      'cursor-pointer': !hasIntegrationUserRole,
    }"
    @mouseenter="isHovered = true"
    @mouseleave="isHovered = false"
    @click="handleRowClick"
  >
    <div
      class="c-population-tab-populations-row__name-cell flex items-center gap-8 px-12 min-h-[48px]"
      :style="nameCellStyle"
    >
      <BittsCheckbox
        v-if="canEdit"
        class="mr-8"
        :checked="selectedPopulationIds.includes(item.population.id)"
        :aria-label="selectCheckboxLabel"
        @input="handlePopulationCheckboxChange(item.population.id, $event)"
        @click.stop
      />
      <div
        v-if="isOpenData"
        class="c-population-tab-populations-row__open-data-icon"
      >
        <FontAwesomeIcon
          :icon="['fak', 'managed-offline-partner']"
          class="text-info-accent text-16"
        />
      </div>
      <BittsAvatar
        v-else
        size="small"
        shape="square"
        :show-border="true"
        :custom-svg="
          item.population?.integration_type
            ? `${item.population?.integration_type}Icon`
            : ''
        "
        class="c-population-tab-populations-row__integration-avatar"
      />
      <BittsTooltip
        placement="bottomLeft"
        class="min-w-0"
        :mount-to-body="true"
        :mouse-enter-delay="0.3"
      >
        <h3 class="font-bold text-neutral-text-strong truncate min-w-0">
          {{ item.population.name }}
        </h3>
        <template #title>
          <p v-if="descriptionText">
            {{ descriptionText }}
          </p>
          <br v-if="descriptionText && processedAtText" />
          <p v-if="processedAtText"> Last updated: {{ processedAtText }}. </p>
        </template>
      </BittsTooltip>
      <BittsTag
        v-if="isOpenData"
        text="Open Data"
        size="x-small"
        color="info"
      />
      <BittsTag
        v-else-if="item.population.standard_type"
        text="Standard"
        size="x-small"
        color="info"
      />
    </div>
    <div class="px-12 flex items-center min-w-0" @click.stop>
      <SharingSettingsVisibilitySelector
        :initial-visibility="item.currentVisibility"
        :aria-label="sharingLevelLabel"
        :disabled="!canEdit"
        :is-greenfield-sharing-blocked="isGreenfieldSharingBlocked"
        :disabled-option-content="{
          text: 'This sharing option was disabled. Please reach out to your admins.',
        }"
        @change="
          (visibility: SharingType) =>
            handleVisibilityChange(item.population.id, visibility)
        "
      />
    </div>
    <div class="px-12 flex items-center min-w-0" @click.stop>
      <SharingSettingsPresetSelector
        v-if="shouldShowPresetSelector"
        :initial-selected-preset-id="localSelectedPresetId"
        :aria-label="fieldPresetsLabel"
        :preset-options="presetOptions"
        :feed-id="feedId"
        :source-id="sourceId"
        :disabled="!canEdit"
        :can-edit-presets="canEdit"
        :is-compact="true"
        @change="handlePresetChange"
        @open-preset-modal="handleOpenPresetModal"
      />
    </div>
    <div class="px-12 flex items-center">
      <BittsTooltip
        v-if="shouldShowUsingDefaultCount"
        placement="top"
        :mouse-enter-delay="0.3"
      >
        <span class="text-neutral-text">
          {{ item.usingDefaultCount }}
          {{ pluralize(item.usingDefaultCount ?? 0, 'Partner') }}
        </span>
        <template #title>
          Click to manage default and custom sharing for all partners.
        </template>
      </BittsTooltip>
    </div>
    <div class="flex items-center justify-center">
      <BittsTooltip
        v-if="!hasIntegrationUserRole"
        placement="top"
        :mouse-enter-delay="0.3"
      >
        <button
          type="button"
          aria-label="View partner sharing settings"
          class="flex cursor-pointer"
          @click.stop="handleRowClick"
        >
          <FontAwesomeIcon
            :icon="['fak', 'chevron-right']"
            class="w-16 h-16"
            :class="isHovered ? 'text-info-accent' : 'text-neutral-accent'"
          />
        </button>
        <template #title> View partner sharing settings </template>
      </BittsTooltip>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  BittsAvatar,
  BittsCheckbox,
  BittsTag,
  BittsTooltip,
} from '@crossbeam/bitts';

import { pluralize } from 'humanize-plus';
import { DateTime } from 'luxon';
import { storeToRefs } from 'pinia';
import { computed, ref, toRef, watch } from 'vue';

import type { PopulationWithSharing } from '@/composables/dataSharingHub/useSharingSettingsHub';
import { useFieldPresetsModal } from '@/composables/useFieldPresetsModal';
import {
  GREENFIELD_SHARING,
  HIDDEN,
  OLAPS_ONLY,
  SHARING,
  SharingType,
} from '@/constants/data_shares';
import { isGoogleOrCsvSource } from '@/constants/data_sources';
import { useDataSharePresetsStore, useSourcesStore } from '@/stores';
import { useRootStore } from '@/stores/RootStore';
import type { PresetOptionsGroup } from '@/types/data_share_presets';
import { isOpenDataPopulation } from '@/types/populations';
import { getFeedIdForPopulation } from '@/utils/getFeedIdForPopulation';

import SharingSettingsPresetSelector from '../SharingSettingsPresetSelector.vue';
import SharingSettingsVisibilitySelector from '../SharingSettingsVisibilitySelector.vue';

import useStickyNameCell from './useStickyNameCell';

type EnrichedPopulation = PopulationWithSharing & {
  currentVisibility?: SharingType;
  currentPresetId?: number | null;
};

const props = withDefaults(
  defineProps<{
    item: EnrichedPopulation;
    selectedPopulationIds: number[];
    hasChanges?: boolean;
    canEdit?: boolean;
    isGreenfieldSharingBlocked?: boolean;
  }>(),
  {
    canEdit: true,
    isGreenfieldSharingBlocked: false,
  },
);

const emit = defineEmits<{
  'visibility-changed': [populationId: number, visibility: SharingType];
  'population-checkbox-changed': [populationId: number, checked: boolean];
  'preset-changed': [populationId: number, presetId: number | undefined];
  'row-clicked': [populationId: number];
}>();

const dataSharePresetsStore = useDataSharePresetsStore();
const sourcesStore = useSourcesStore();
const { openModal } = useFieldPresetsModal();
const { hasIntegrationUserRole } = storeToRefs(useRootStore());
const isOpenData = computed(() => isOpenDataPopulation(props.item.population));

const selectCheckboxLabel = computed(
  () => `Select ${props.item.population.name}`,
);
const sharingLevelLabel = computed(
  () => `Sharing Level, ${props.item.population.name}`,
);
const fieldPresetsLabel = computed(
  () => `Field Presets, ${props.item.population.name}`,
);

const shouldShowUsingDefaultCount = computed(
  () => !!props.item.usingDefaultCount && !hasIntegrationUserRole.value,
);

const localSelectedPresetId = ref<number | null>(
  props.item.currentPresetId ?? null,
);

const localVisibility = ref<SharingType | undefined>(
  props.item.currentVisibility,
);

watch(
  () => props.item.currentPresetId,
  (newPresetId) => {
    localSelectedPresetId.value = newPresetId ?? null;
  },
);

watch(
  () => props.item.currentVisibility,
  (newVisibility) => {
    localVisibility.value = newVisibility;
  },
);

watch(localVisibility, (newVisibility, oldVisibility) => {
  const switchingToSharing =
    (newVisibility === SHARING || newVisibility === GREENFIELD_SHARING) &&
    (oldVisibility === HIDDEN ||
      oldVisibility === OLAPS_ONLY ||
      !oldVisibility);

  const switchingFromSharing =
    (oldVisibility === SHARING || oldVisibility === GREENFIELD_SHARING) &&
    (newVisibility === HIDDEN || newVisibility === OLAPS_ONLY);

  if (switchingToSharing && !localSelectedPresetId.value) {
    const firstPresetGroup = basePresetOptions.value[0];
    const firstPreset = firstPresetGroup?.items[0];
    if (firstPreset && typeof firstPreset.value === 'number') {
      localSelectedPresetId.value = firstPreset.value;
      handlePresetChange(firstPreset.value);
    }
  }

  if (switchingFromSharing) {
    const originalPresetId = props.item.preset?.id ?? undefined;
    const currentPresetId = localSelectedPresetId.value;
    const targetPresetId = originalPresetId ?? null;
    if (currentPresetId !== targetPresetId) {
      localSelectedPresetId.value = targetPresetId;
      handlePresetChange(originalPresetId);
    }
  }
});

const isGoogleOrCsv = computed(() =>
  isGoogleOrCsvSource(props.item.population.integration_type),
);

const feedId = computed(() =>
  getFeedIdForPopulation(props.item.population, (id) =>
    sourcesStore.getSourceById(id),
  ),
);

const sourceId = computed(() => {
  return isGoogleOrCsv.value ? props.item.population.source_id : undefined;
});

const selectedSingleSharePreset = computed(() => {
  if (!feedId.value || !localSelectedPresetId.value) return null;

  const filteredPresets = dataSharePresetsStore.getFilteredPresetsByFeeId(
    feedId.value,
    sourceId.value,
  );

  return filteredPresets.find(
    (preset) =>
      preset.id === localSelectedPresetId.value &&
      preset.preset_type === 'single_share_preset',
  );
});

const basePresetOptions = computed((): PresetOptionsGroup[] => {
  if (!feedId.value) return [];

  const currentFeedId = feedId.value;
  const filteredPresets = dataSharePresetsStore.getFilteredPresetsByFeeId(
    currentFeedId,
    sourceId.value,
  );

  const allOptions =
    dataSharePresetsStore.getPresetOptionsPerFeed(filteredPresets);
  const parentId = sourceId.value ?? currentFeedId;
  return allOptions[parentId] ?? [];
});

const presetOptions = computed((): PresetOptionsGroup[] => {
  const baseOptions = basePresetOptions.value;

  if (!selectedSingleSharePreset.value) return baseOptions;

  return [
    ...baseOptions,
    {
      heading: null,
      items: [
        {
          value: selectedSingleSharePreset.value.id,
          label: 'Custom Field Selection',
          presetType: 'single_share_preset',
        },
      ],
    },
  ];
});

const shouldShowPresetSelector = computed(() => {
  return (
    localVisibility.value === SHARING ||
    localVisibility.value === GREENFIELD_SHARING
  );
});

function handleVisibilityChange(populationId: number, visibility: SharingType) {
  localVisibility.value = visibility;
  emit('visibility-changed', populationId, visibility);
}

function handlePopulationCheckboxChange(
  populationId: number,
  checked: boolean,
) {
  emit('population-checkbox-changed', populationId, checked);
}

function handlePresetChange(presetId: number | undefined) {
  localSelectedPresetId.value = presetId ?? null;
  emit('preset-changed', props.item.population.id, presetId);
}

function handleOpenPresetModal(presetId: number | undefined) {
  if (!feedId.value) return;
  openModal({
    feedId: feedId.value,
    presetId,
    sourceId: sourceId.value,
    isForPartner: false,
    onSuccess: (newPresetId: number, isNewPreset: boolean) => {
      if (isNewPreset) {
        localSelectedPresetId.value = newPresetId;
        handlePresetChange(newPresetId);
      }
    },
  });
}

function handleRowClick() {
  if (hasIntegrationUserRole.value) return;
  emit('row-clicked', props.item.population.id);
}

const processedAtText = computed(() => {
  if (!props.item.population.meta?.updated_at) return null;
  const dateTime = DateTime.fromISO(props.item.population.meta.updated_at);
  return dateTime.isValid ? dateTime.toFormat('LLLL d, yyyy') : null;
});

const descriptionText = computed(() => {
  if (!props.item.population.description) return null;
  return props.item.population.description;
});

const { isHovered, nameCellStyle } = useStickyNameCell({
  hasChanges: toRef(() => props.hasChanges ?? false),
});
</script>

<style lang="pcss" scoped>
@reference "../../../css/theme.css";

.c-population-tab-populations-row {
  @apply grid items-center p-4 w-full gap-4;
  grid-template-columns:
    minmax(250px, 2.5fr) minmax(240px, 1.5fr) minmax(240px, 1.5fr)
    minmax(120px, 1.5fr) 40px;
}

.c-population-tab-populations-row--modified {
  background-color: rgba(221, 222, 255, 0.25);
}

.c-population-tab-populations-row__name-cell {
  @apply sticky left-0 z-[2];
  margin-left: -4px;
  padding-left: 16px;
}

:deep(.c-population-tab-populations-row__integration-avatar) {
  span {
    @apply !bg-white;
  }
}

.c-population-tab-populations-row__open-data-icon {
  @apply size-[24px] rounded-[4px] border border-solid border-neutral-border
    bg-white flex items-center justify-center shrink-0;
}
</style>
